import StyledButton from "components/shared/StyledButton";
import { PortalType } from "contexts/portal.context";
import { Body, Heading2, SmallBody } from "helpers/TypographyComponents";
import { useContext } from "react";
import { useTranslation } from "react-i18next";

import styles from "./style.module.scss";

export type AvenantTarget = "default" | "frameworkContract";

interface Props {
    value: AvenantTarget | null;
    onChange: (value: AvenantTarget) => void;
    onClose: () => void;
    onContinue: () => void;
}

const TitleIcon = () => (
    <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <rect x="4" y="3" width="16" height="18" rx="2" />
        <path d="M8 8h8M8 12h8M8 16h5" />
    </svg>
);

const FrameworkIcon = () => (
    <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="M3 10h18M8 15h4" />
    </svg>
);

const AvenantTargetStep = ({ value, onChange, onClose, onContinue }: Props) => {
    const [t] = useTranslation();
    const portalType = useContext(PortalType);
    const suffix = portalType === "press" || portalType === "audiovisual" ? "press" : "book";

    const tiles: Array<{
        id: AvenantTarget;
        title: string;
        description: string;
        icon: JSX.Element;
    }> = [
        {
            id: "default",
            title: t(`ContractSettings.AvenantTargetDefault_${suffix}`),
            description: t(`ContractSettings.AvenantTargetDefaultDescription_${suffix}`),
            icon: <TitleIcon />,
        },
        {
            id: "frameworkContract",
            title: t("ContractSettings.AvenantTargetFramework"),
            description: t("ContractSettings.AvenantTargetFrameworkDescription"),
            icon: <FrameworkIcon />,
        },
    ];

    return (
        <div className={styles["target-step"]}>
            <div className={styles["wrapper"]}>
                <Heading2>{t("ContractSettings.AvenantTargetQuestion")}</Heading2>
                <Body color="info">{t("ContractSettings.AvenantTargetHint")}</Body>
                <div className={styles["tiles"]}>
                    {tiles.map((tile) => {
                        const selected = value === tile.id;
                        return (
                            <button
                                key={tile.id}
                                type="button"
                                className={`${styles["tile"]} ${selected ? styles["tile-selected"] : ""}`}
                                onClick={() => onChange(tile.id)}
                            >
                                <span className={styles["tile-icon"]}>{tile.icon}</span>
                                <span className={styles["tile-text"]}>
                                    <Body style={{ margin: 0, fontWeight: 600 }}>{tile.title}</Body>
                                    <SmallBody color="grey" style={{ margin: "4px 0 0" }}>
                                        {tile.description}
                                    </SmallBody>
                                </span>
                                <span className={`${styles["tile-radio"]} ${selected ? styles["tile-radio-on"] : ""}`}>
                                    <span className={styles["tile-radio-inner"]} />
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>
            <div className={styles["actions"]}>
                <StyledButton buttonType="secondary" onClick={onClose}>
                    {t("Cancel")}
                </StyledButton>
                <StyledButton buttonType="primary" onClick={onContinue} disabled={!value}>
                    {t("NewElement.Continue")}
                </StyledButton>
            </div>
        </div>
    );
};

export default AvenantTargetStep;
